import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { eraseCookie } from "@/tools/cookie";
import router from "@/router";
import { type UserDto, UserRole } from "@/api/api.dto";
import { usePartner } from "@/stores/partner/partner";
import { useClaims } from "@/stores/staticCatalog/claims";
import { useProductsStore } from "@/stores/product/products/products";
import AUTH_API from "@/api/classes/auth";
import USER_API from "@/api/classes/user";
import { useCustomCategoriesStore } from "@/stores/customCategories/customCategories";
import { enumStatusLoad } from "@/tools/enumStatusLoad";
import { createNotification } from "@/tools/notification";
import { Claim, ClaimsTreeMap, RootClaims } from "@/common/claims/claimsEnum";

export const useUser = defineStore("user", () => {
  const partnerStore = usePartner();
  const claimsStore = useClaims();
  const productStore = useProductsStore();
  const customCategoriesStore = useCustomCategoriesStore();
  const statusLoadLogout = ref(enumStatusLoad.NOT_STARTED);
  const deleteUserStatus = ref(enumStatusLoad.NOT_STARTED);
  const changePasswordStatus = ref(enumStatusLoad.NOT_STARTED);
  const updateFullNameStatus = ref(enumStatusLoad.NOT_STARTED);

  const user = ref<UserDto | null>(null);
  const token = ref<string | null>(null);

  const userIsLoggined = computed(() => {
    return !!user.value;
  });

  const userClaimsSet = computed(() => {
    return claimsStore.parseClaims(user.value?.claims || "");
  });

  const isUserOwner = computed(() => user.value?.role === UserRole.Owner);
  const isUserEmployee = computed(() => user.value?.role === UserRole.Employee);
  const isUserSupport = computed(() => user.value?.role === UserRole.Support);

  const refreshAuth = async () => {
    try {
      const { data } = await AUTH_API.postRefresh();

      if (data) {
        token.value = data.accessToken;
        await getUser(data.id);

        return data;
      }

      return null;
    } catch (err) {
      console.error("Error refreshing auth:", err);
    }
  };
  const getUser = async (id: string) => {
    try {
      if (id) {
        const response = await USER_API.getUser(id);

        if (response.status === 200) {
          user.value = { ...response.data };
        }
      }

      return null;
    } catch (err) {
      console.error("Error refreshing auth:", err);
    }
  };

  const hasClaim = (claim: Claim): boolean => {
    if (!user.value) {
      return false;
    }
    if (userClaimsSet.value.has(Claim.FullAccess)) {
      return true;
    }
    if (isUserOwner.value) {
      return true;
    }

    return (
      userClaimsSet.value.has(claim) ||
      (RootClaims.has(claim) &&
        userClaimsSet.value.intersection(
          new Set(ClaimsTreeMap[claim as keyof typeof ClaimsTreeMap]),
        ).size > 0)
    );
  };

  const hasRole = (roles: UserRole[]): boolean => {
    if (!user.value) {
      return false;
    }
    if (
      !user.value.role &&
      user.value.role !== 0 &&
      user.value.role !== UserRole.Support
    ) {
      return false;
    }
    return roles.includes(user.value.role);
  };

  const logout = async () => {
    if (!userIsLoggined.value) {
      return;
    }

    try {
      statusLoadLogout.value = enumStatusLoad.LOADING;

      await AUTH_API.logOut();
      globalReset();

      await router.push({ name: "login" });

      statusLoadLogout.value = enumStatusLoad.LOADED;
    } catch (e) {
      console.error(e);
      statusLoadLogout.value = enumStatusLoad.ERROR;
    }
  };

  const deleteUser = async (userId: UserDto["id"]) => {
    deleteUserStatus.value = enumStatusLoad.LOADING;

    try {
      await USER_API.deleteUser(userId);
      deleteUserStatus.value = enumStatusLoad.LOADED;

      return true;
    } catch (e) {
      console.error(e);
      deleteUserStatus.value = enumStatusLoad.ERROR;
      return false;
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    changePasswordStatus.value = enumStatusLoad.LOADING;

    try {
      await USER_API.changePassword(oldPassword, newPassword);
      changePasswordStatus.value = enumStatusLoad.LOADED;

      createNotification("success", "Пароль успешно изменен!");

      return true;
    } catch (e: unknown) {
      console.log(e);

      createNotification(
        "error",
        "Не удалось изменить пароль",
        // @ts-ignore
        e.response.data.toString(),
      );
      changePasswordStatus.value = enumStatusLoad.ERROR;
      return false;
    }
  };

  const updateFullName = async (newFullName: string, userId: UserDto["id"]) => {
    updateFullNameStatus.value = enumStatusLoad.LOADING;

    try {
      await USER_API.updateFullName(newFullName, userId);
      updateFullNameStatus.value = enumStatusLoad.LOADED;

      return true;
    } catch (e: unknown) {
      console.log(e);

      createNotification(
        "error",
        "Не удалось изменить ФИО",
        // @ts-ignore
        e.response.data.toString(),
      );
      updateFullNameStatus.value = enumStatusLoad.ERROR;
      return false;
    }
  };

  const globalReset = () => {
    $reset();
    partnerStore.$reset();
    productStore.$reset();
    customCategoriesStore.$reset();

    eraseCookie("AuthToken");
    eraseCookie("RefreshToken");
  };

  function $reset() {
    user.value = null;
    token.value = null;
  }

  return {
    user,
    refreshAuth,
    logout,
    userIsLoggined,
    getUser,
    hasClaim,
    globalReset,
    statusLoadLogout,
    deleteUser,
    changePassword,
    changePasswordStatus,
    deleteUserStatus,
    updateFullName,
    updateFullNameStatus,
    isUserOwner,
    isUserEmployee,
    isUserSupport,
    hasRole,
    token,
    userClaimsSet,
  };
});
